import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import {
  faPlus,
  faCancel,
  faPencil,
} from "@fortawesome/free-solid-svg-icons";
import { FormField } from "../../../../interfaces/form-field.model";
import { NotifierService } from "angular-notifier";
import { SpecialPriceListMasterService } from "src/app/services/sales/special-price-list-master.service";
import { formatDate } from "@angular/common";
import { getEmptyGuid } from "src/app/services/util.service";
import {
  AddSpecialPrice,
  model,
} from "src/app/models/sales/special-price-list-master";

@Component({
  selector: "app-special-price-list-master-detail",
  templateUrl: "./special-price-list-master-detail.component.html",
  styleUrls: ["./special-price-list-master-detail.component.css"],
})
export class SpecialPriceListMasterDetailComponent implements OnInit {
  faPlus = faPlus;
  faCancel = faCancel;
  faPencil = faPencil;

  mode: string = "Add";
  lockUpdate: boolean = true;

  __type: string = "";
  Id: string = "0";
  MODELCODE: string = "";

  availableModels: any[] = [];

  private readonly numericFieldIds = [
    "ENGINECAP",
    "LUCKYDRAW",
    "DIPLOMAT",
    "USARMY",
    "HANDICAP",
    "PPLATE",
    "SPLATE",
    "OWNCOE",
  ];

  formFields: FormField[] = [
    {
      type: "select",
      id: "MODELSHORTDESC",
      label: "Model Short Desc",
      required: true,
      options: [],
    },
    {
      type: "text",
      id: "MODELGROUP",
      label: "Model Group",
      required: true,
    },
    {
      type: "number",
      id: "ENGINECAP",
      label: "Eng. Cap",
      min: 0,
      max: 999999,
    },
    {
      type: "number",
      id: "LUCKYDRAW",
      label: "Lucky Draw",
      required: true,
      min: 0,
      max: 999999,
    },
    {
      type: "number",
      id: "DIPLOMAT",
      label: "Diplomat",
      required: true,
      min: 0,
      max: 999999,
    },
    {
      type: "number",
      id: "USARMY",
      label: "US Army",
      required: true,
      min: 0,
      max: 999999,
    },
    {
      type: "number",
      id: "HANDICAP",
      label: "Handicap",
      required: true,
      min: 0,
      max: 999999,
    },
    {
      type: "number",
      id: "PPLATE",
      label: "Handicap (Starex Minibus - P plate)",
      min: 0,
      max: 999999,
    },
    {
      type: "number",
      id: "SPLATE",
      label: "Handicap (Starex Minibus - S plate/ P plate VWO)",
      min: 0,
      max: 999999,
    },
    {
      type: "number",
      id: "OWNCOE",
      label: "Own COE",
      required: true,
      min: 0,
      max: 999999,
    },
    {
      type: "date",
      id: "EFFECTIVEDATE",
      label: "Effective Date",
      required: true,
    },
  ];

  constructor(
    private specialPriceListMasterService: SpecialPriceListMasterService,
    private notifier: NotifierService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit() {
    this.loadActiveModelList();
    this.route.params.subscribe((params) => {
      this.Id = params["id"];
      if (this.Id !== "0") {
        this.mode = "Edit";
        this.lockUpdate = false;
        this.loadModelDetails();
      }
    });
  }

  private loadModelDetails(): void {
    this.specialPriceListMasterService.GetDetails(this.Id).subscribe({
      next: (response) => {
        if (!response.body) {
          this.notifier.notify(
            "error",
            "Special Price List record was not found",
          );
          this.onCancel();
          return;
        }
        const { __type, Id, MODELCODE, ...data } = response.body;
        this.__type = __type ?? "";
        this.MODELCODE = MODELCODE;
        Object.entries(data).forEach(([key, value]) => {
          if (key == "EFFECTIVEDATE") {
            this.getField(key).value = this.dateToField(
              value as string,
            );
            return;
          }
          this.getField(key).value = value;
        });
        this.getField("MODELSHORTDESC").lock = true;
        this.getField("MODELGROUP").lock = true;
        this.ensureCurrentModelOption();
        this.checkCanUpdate();
      },
      error: (error: any) => {
        console.error("Error loading model details:", error);
        this.notifier.notify(
          "error",
          "Unable to load Special Price List details",
        );
      },
    });
  }

  loadActiveModelList(): void {
    this.specialPriceListMasterService
      .GetBasicActiveModelList()
      .subscribe({
        next: (response) => {
          this.availableModels = response.body;
          this.getField("MODELSHORTDESC").options = response.body.map(
            (item) => ({
              display: item.ModelShortDesc,
              value: item.ModelShortDesc,
            }),
          );
          this.ensureCurrentModelOption();
        },
        error: (error) => {
          console.error(error);
          this.notifier.notify(
            "error",
            "Unable to load the active model list",
          );
        },
      });
  }

  updateFields(event: any) {
    if (event.updatedValue == 0) {
      const newModel = this.availableModels.find(
        (item) =>
          item.ModelShortDesc ==
          this.getField("MODELSHORTDESC").value,
      );
      if (newModel) {
        this.getField("MODELGROUP").value = newModel.ModelShort;
      }
    }
    this.checkCanUpdate();
  }

  checkCanUpdate(): void {
    this.lockUpdate = !this.formFields
      .filter((item) => item.required == true)
      .every(
        (item) =>
          item.value !== undefined &&
          item.value !== null &&
          item.value !== "",
      );
  }

  onCancel(): void {
    this.router.navigate(["/sales/special-price-list-master"]);
  }

  handlePriceList(action: "Add" | "Update"): void {
    if (!this.hasValidPrices()) {
      this.notifier.notify(
        "error",
        "Amounts must be between 0 and 999999",
      );
      return;
    }

    const priceModel = this.buildFormPayload();

    if (action === "Add") {
      priceModel["Id"] = getEmptyGuid();
      this.specialPriceListMasterService
        .Add(priceModel as AddSpecialPrice)
        .subscribe({
          next: (response) => {
            if (response.code !== 0) {
              this.notifier.notify("error", response.message);
            } else {
              this.notifier.notify(
                "success",
                "Special Price List has been added successfully",
              );
              this.onCancel();
            }
          },
          error: (error) => {
            console.error(error);
            this.notifier.notify(
              "error",
              "Unable to add Special Price List",
            );
          },
        });
    } else if (action === "Update") {
      priceModel["__type"] = this.__type;
      priceModel["Id"] = this.Id;
      priceModel["MODELCODE"] = this.MODELCODE;
      this.specialPriceListMasterService
        .Update(priceModel as model)
        .subscribe({
          next: (response) => {
            if (response.code !== 0) {
              this.notifier.notify("error", response.message);
              return;
            }
            this.notifier.notify(
              "success",
              "Special Price List has been updated successfully",
            );
            this.onCancel();
          },
          error: (error) => {
            console.error(error);
            this.notifier.notify(
              "error",
              "Unable to update Special Price List",
            );
          },
        });
    }
  }

  private buildFormPayload(): Record<string, any> {
    return this.formFields.reduce((payload, field) => {
      if (field.id === "EFFECTIVEDATE") {
        payload[field.id] = formatDate(
          field.value,
          "dd/MM/yyyy",
          "en-US",
        );
      } else if (this.numericFieldIds.includes(field.id)) {
        payload[field.id] =
          field.value === null ||
          field.value === undefined ||
          field.value === ""
            ? null
            : Number(field.value);
      } else {
        payload[field.id] = field.value ?? null;
      }
      return payload;
    }, {} as Record<string, any>);
  }

  private ensureCurrentModelOption(): void {
    if (this.mode !== "Edit") {
      return;
    }

    const modelShortDesc = this.getField("MODELSHORTDESC").value;
    const field = this.getField("MODELSHORTDESC");
    if (
      modelShortDesc &&
      !field.options.some(
        (option: any) => option.value === modelShortDesc,
      )
    ) {
      field.options = [
        { display: modelShortDesc, value: modelShortDesc },
        ...field.options,
      ];
    }
  }

  private hasValidPrices(): boolean {
    return this.formFields
      .filter((field) => field.type === "number")
      .every((field) => {
        if (
          field.value === null ||
          field.value === undefined ||
          field.value === ""
        ) {
          return !field.required;
        }
        const value = Number(field.value);
        return Number.isFinite(value) && value >= 0 && value <= 999999;
      });
  }

  dateToField(date: string): string {
    const [day, month, year] = date.split("/");
    if (!day || !month || !year) {
      throw new Error("Date has no valid format");
    }
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }

  getField(id: string): any {
    const field = this.formFields.find((f) => f.id === id);
    return field;
  }
}
